import React, { useCallback, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Display,
  FlexDirection,
  Grid,
  JustifyContent,
} from '@gousto-internal/citrus-react'
import Overlay from 'Overlay'
import { SocialShareSheetContainer } from 'SocialLinks/SocialShareSheet'
import { Map as ImmutableMap } from 'immutable'

import { getTimeDifference } from '../timeHelper'
import { RAFBoostCountdown } from './RAFBoostCountdown'
import { RAFBoostHeader } from './RAFBoostHeader'
import { RAFBoostInstructions } from './RAFBoostInstructions'
import { RAFBoostOfferBoxes } from './RAFBoostOfferBoxes'
import { RAFBoostShare } from './RAFBoostShare'

const AlignedContainer = styled(Box)`
  @media (min-width: 886px) {
    max-width: min-content;
  }
  @media (max-width: 886px) {
    margin: 0 16px;
  }
  padding-bottom: 40px;
`

const StickyMobileShareButton = styled(Box)`
  padding: 16px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 2;

  @media (min-width: 886px) {
    display: none;
  }
`

const RelativeBox = styled(Box)`
  position: relative;
  margin-bottom: -25px;
`

const FullWidthButton = styled(Button)`
  padding: 8px 16px;
  width: 100%;
`

interface Props {
  rafOffer: ImmutableMap<
    string,
    {
      creditFormatted: string
      firstBoxDiscountFormatted: string
      firstMonthDiscountFormatted: string
      originalCampaignDetails: ImmutableMap<string, string>
    }
  >
  expiry: Date
  userRAFLink: string
  userFirstName: string
  device: string
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: () => void
  trackingReferFriend: () => void
  referralCode: string
}

export const BoostedRafSharing = ({
  rafOffer,
  userFirstName,
  device,
  userRAFLink,
  expiry,
  trackUserFreeFoodLinkShare,
  trackingReferFriendSocialSharing,
  trackingReferFriend,
  referralCode,
}: Props) => {
  const { days, hours, minutes, seconds } = getTimeDifference(expiry)
  const [expiryDays, setExpiryDays] = useState<number>(days)
  const [expiryHours, setExpiryHours] = useState<number>(hours)
  const [expiryMinutes, setExpiryMinutes] = useState<number>(minutes)
  const [expirySeconds, setExpirySeconds] = useState<number>(seconds)
  const [isShareSheetOpen, setIsShareSheetOpen] = useState<boolean>(false)

  const onMobileShare = useCallback(() => {
    if (navigator.share) {
      navigator
        .share({
          title: "Here's my link to try Gousto!",
          url: userRAFLink,
          text: 'Use it to get 70% off your first box, PLUS 25% off all other boxes in your first two months.',
        })
        .catch(() => {})
    } else {
      setIsShareSheetOpen(true)
    }

    trackUserFreeFoodLinkShare()
  }, [trackUserFreeFoodLinkShare, userRAFLink])

  const closeShareSheet = useCallback(() => {
    setIsShareSheetOpen(false)
    trackingReferFriendSocialSharing()
  }, [trackingReferFriendSocialSharing])

  const setTimes = useCallback(() => {
    const timeDifference = getTimeDifference(expiry)
    setExpiryDays(timeDifference.days)
    setExpiryHours(timeDifference.hours)
    setExpiryMinutes(timeDifference.minutes)
    setExpirySeconds(timeDifference.seconds)
  }, [expiry])

  useEffect(() => {
    setInterval(() => {
      setTimes()
    }, 1000)
  }, [setTimes])

  return (
    <RelativeBox>
      <Grid alignItems={AlignItems.Center} justifyContent={JustifyContent.Center}>
        <Col
          size={[12, 8, 8, 8]}
          gridColumn={['1 / span 12', '3 / span 8', '3 / span 8', '3 / span 8']}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
        >
          <AlignedContainer
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.Center}
            alignItems={AlignItems.Center}
          >
            <Box paddingBottom={6}>
              <RAFBoostHeader />
              <RAFBoostOfferBoxes rafOffer={rafOffer} />
            </Box>
            <RAFBoostCountdown
              days={expiryDays}
              minutes={expiryMinutes}
              hours={expiryHours}
              seconds={expirySeconds}
            />
            <RAFBoostShare
              userRAFLink={userRAFLink}
              userFirstName={userFirstName}
              trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
              device={device}
              trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
              trackingReferFriend={trackingReferFriend}
            />
          </AlignedContainer>
        </Col>
      </Grid>
      <Grid>
        <Col size={12}>
          <RAFBoostInstructions />
        </Col>
      </Grid>
      <StickyMobileShareButton>
        <FullWidthButton
          colorVariant={ButtonColorVariant.Primary}
          onClick={onMobileShare}
          width="100%"
        >
          Share your link
        </FullWidthButton>
      </StickyMobileShareButton>
      <Overlay open={isShareSheetOpen} from="bottom">
        <SocialShareSheetContainer onClose={() => closeShareSheet()} referralCode={referralCode} />
      </Overlay>
    </RelativeBox>
  )
}
